import type { SVGProps } from "react";

import { LEGACY_ICONS, type LegacyIconName } from "../lib/legacy-icons";

/**
 * Font Awesome 7 icon name -> glyph in the legacy set.
 *
 * Call sites name icons with whatever `@fortawesome/free-solid-svg-icons`
 * exports, but the artwork drawn is the legacy app's Font Awesome 4.7 glyph, so
 * the two apps render the same icon. Add an entry here when a call site reaches
 * for an icon this map does not cover yet; an unmapped name throws rather than
 * rendering an empty box, so a gap surfaces immediately (the legacy app takes
 * the opposite tack in production and renders nothing).
 */
const FONT_AWESOME_7_TO_LEGACY = {
  "angle-right": "angle-right",
  "arrow-up-right-from-square": "external-link",
  "arrows-left-right": "arrows-h",
  bars: "bars",
  "calendar-days": "calendar",
  "caret-down": "caret-down",
  "caret-left": "caret-left",
  "caret-right": "caret-right",
  "caret-up": "caret-up",
  check: "check",
  clock: "clock-o",
  copy: "copy",
  desktop: "desktop",
  download: "download",
  "ellipsis-h": "ellipsis-h",
  eye: "eye",
  file: "file",
  "floppy-disk": "save",
  "magnifying-glass": "search",
  moon: "moon-o",
  "paper-plane": "paper-plane",
  "pen-to-square": "edit",
  pencil: "pencil",
  plus: "plus",
  "right-from-bracket": "sign-out",
  "square-check": "check-square-o",
  sun: "sun-o",
  trash: "trash",
  user: "user",
  xmark: "remove",
} as const satisfies Record<string, LegacyIconName>;

export type LegacyFontAwesomeIconName = LegacyIconName;

/** `'cog'` or `'fa-cog'` — the legacy app's @fa() accepts either. */
type LegacyName = LegacyIconName | `fa-${LegacyIconName}`;

interface FontAwesomeIconDefinition {
  iconName: string;
}

type LegacyFontAwesomeIconProps = SVGProps<SVGSVGElement> &
  (
    | { name: LegacyName; icon?: never }
    | { icon: FontAwesomeIconDefinition; name?: never }
  );

export function LegacyFontAwesomeIcon(props: LegacyFontAwesomeIconProps) {
  // ref and children are accepted for JSX compatibility but intentionally not
  // rendered: the icon is a fixed svg + path. Omitting them from the spread
  // keeps the svg props exactly SVGProps (extra keys break assignability).
  const {
    name,
    icon: fontAwesomeIcon,
    ref: _ref,
    children: _children,
    ...svgProps
  } = props;

  let iconName: LegacyIconName | undefined;
  if (name) {
    iconName = name.replace(/^fa-/, "") as LegacyIconName;
  } else if (fontAwesomeIcon) {
    iconName =
      FONT_AWESOME_7_TO_LEGACY[
        fontAwesomeIcon.iconName as keyof typeof FONT_AWESOME_7_TO_LEGACY
      ];
  }
  if (!iconName || !(iconName in LEGACY_ICONS)) {
    throw new Error(
      `No legacy Font Awesome 4.7 glyph for "${fontAwesomeIcon?.iconName ?? name}". ` +
        `Map it in packages/ui/src/components/legacy-font-awesome-icon.tsx — the ` +
        `glyphs themselves are in packages/ui/src/lib/legacy-icons.ts, ported ` +
        `from the legacy app's sprite (pnpm --filter @workspace/ui icons:build).`
    );
  }

  const icon = LEGACY_ICONS[iconName];
  const ariaHidden = svgProps["aria-hidden"];

  return (
    <svg
      {...svgProps}
      aria-hidden={ariaHidden ?? true}
      fill="currentColor"
      focusable="false"
      viewBox={icon.viewBox}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={icon.path} transform="scale(1,-1) translate(0 -1536)" />
    </svg>
  );
}
